import { createContext, ReactNode, useContext, useMemo } from 'react';

import { routeToPage } from 'support/helpers/navigation/navigation';

export type ConnectionOrigin = 'connections' | 'network';

const ConnectionSubPaths = {
  connectionDetails: '/partner/:partnerId',
  relationshipDetails: '/partner/:partnerId/relationship/:relationshipId',
  relationshipTransactions: '/partner/:partnerId/relationship/:relationshipId/transactions',
  relationshipSetup: '/partner/:partnerId/relationship/:relationshipId/setup',
  relationshipNotifications: '/partner/:partnerId/relationship/:relationshipId/notifications',
  testRelationship: '/partner/:partnerId/relationship/:relationshipId/test',
  testRelationshipMessageDetails: '/partner/:partnerId/relationship/:relationshipId/test/message/:messageId',
  testRelationshipTransactionError:
    '/partner/:partnerId/relationship/:relationshipId/test/transaction/:transactionId/error',
  goLiveRelationship: '/partner/:partnerId/relationship/:relationshipId/go-live',
  instantConnectivity: '/partner/:partnerId/relationship/:relationshipId/instant-connectivity',
  taskDetails: '/partner/:partnerId/relationship/:relationshipId/task/:taskId',
} as const;

type SubPath = keyof typeof ConnectionSubPaths;

type ConnectionNavigationContextValue = {
  origin: ConnectionOrigin;
  getRoute: (subPath: SubPath) => string;
  routeTo: (subPath: SubPath, params: Record<string, string>) => string;
};

const ConnectionNavigationContext = createContext<ConnectionNavigationContextValue | null>(null);

export const ConnectionNavigationProvider = ({
  origin,
  children,
}: {
  origin: ConnectionOrigin;
  children: ReactNode;
}) => {
  const value = useMemo(() => {
    const prefix = origin === 'network' ? '/network' : '/connections';

    const getRoute = (subPath: SubPath) => `${prefix}${ConnectionSubPaths[subPath]}`;
    const routeTo = (subPath: SubPath, params: Record<string, string>) => routeToPage(getRoute(subPath), params);

    return { origin, getRoute, routeTo };
  }, [origin]);

  return <ConnectionNavigationContext.Provider value={value}>{children}</ConnectionNavigationContext.Provider>;
};

export const useConnectionNavigation = () => {
  const context = useContext(ConnectionNavigationContext);
  if (!context) {
    throw new Error('useConnectionNavigation must be used within ConnectionNavigationProvider');
  }
  return context;
};
